import {
  FilterOperatorTypes,
  FilterType,
  filterOperators
} from '~/types/filters';
import { DataTableColumn } from '../data-table.types';
import { getDataType } from '../utils/filter-operations';
import { useDataTable } from './useDataTable';

export function useFilters<TData, TValue>() {
  const { updateTableQuery } = useDataTable();

  function onAddFilter(column: DataTableColumn<TData, TValue>) {
    const columnDef = column.columnDef;
    const id = columnDef.accessorKey || column.id;
    const options = columnDef.filterOptions || [];
    const filterType = columnDef.filterType || FilterType.string;
    const dataType = getDataType({ filterType, dataType: columnDef.dataType });
    const defaultFilter = filterOperators[filterType][0];
    const defaultValue =
      columnDef.defaultFilterValue ??
      (filterType === FilterType.date
        ? new Date()
        : filterType === FilterType.select
          ? options[0]?.value
          : filterType === FilterType.multiselect
            ? // first option preselected like select; [] when there are no options
              options
                .slice(0, 1)
                .map(opt => opt.value)
            : '');

    updateTableQuery(query => {
      return {
        ...query,
        filters: [
          ...(query.filters || []),
          {
            _dataType: dataType,
            _type: filterType,
            name: id,
            value: defaultValue,
            operator: defaultFilter.value
          }
        ]
      };
    });
  }

  function handleRemoveFilter(columnId: string) {
    updateTableQuery(query => {
      return {
        ...query,
        filters: query.filters?.filter(filter => filter.name !== columnId)
      };
    });
  }

  function handleFilterValueChange(columnId: string, value: unknown) {
    updateTableQuery(query => {
      return {
        ...query,
        filters: query.filters?.map(filter => {
          if (filter.name === columnId) {
            return { ...filter, value };
          }
          return filter;
        })
      };
    });
  }

  function handleFilterOperationChange(
    columnId: string,
    operator: FilterOperatorTypes
  ) {
    updateTableQuery(query => {
      return {
        ...query,
        filters: query.filters?.map(filter => {
          if (filter.name === columnId) {
            return { ...filter, operator };
          }
          return filter;
        })
      };
    });
  }

  return {
    onAddFilter,
    handleRemoveFilter,
    handleFilterValueChange,
    handleFilterOperationChange
  };
}
